import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, MessageSquare, User } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = { pending: "Pending", quoted: "Quoted", in_print: "In Print", delivered: "Delivered" };
const STATUS_COLORS = { pending: "text-yel", quoted: "text-cyanbrand", in_print: "text-mag", delivered: "text-emerald-400" };

export default function Account() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("signin");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch {}
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
      setChecking(false);
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/enquiries/mine`, { withCredentials: true })
      .then(({ data }) => setEnquiries(data))
      .catch(() => {});
  }, [user]);

  const googleLogin = () => {
    sessionStorage.setItem("auth_return", "/account");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/account")}`;
  };

  const afterAuth = async () => {
    const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(data);
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      await afterAuth();
      toast.success("Signed in");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not sign in. Check your email and password.");
    }
  };

  const register = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, reg);
      await axios.post(`${API}/auth/login`, { email: reg.email, password: reg.password }, { withCredentials: true });
      await afterAuth();
      toast.success("Account created — welcome!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create the account. Try again.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
    setEnquiries([]);
  };

  const inputCls =
    "w-full bg-surface border border-edge rounded-sm px-4 py-3.5 text-base text-paper placeholder:text-mist/50 focus:outline-none focus:border-cyanbrand transition-colors";
  const labelCls = "block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2";

  if (checking) {
    return (
      <div className="min-h-screen bg-ink grid place-items-center font-body">
        <p className="font-mono text-xs text-mist animate-pulse">CHECKING SESSION…</p>
      </div>
    );
  }

  const googleBtn = (
    <button
      type="button"
      data-testid="account-google-button"
      onClick={googleLogin}
      className="w-full inline-flex items-center justify-center gap-3 bg-white text-ink font-semibold text-sm py-3.5 rounded-sm hover:bg-paper transition-colors"
    >
      <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5h-4v3.1C3.3 21.3 7.3 24 12 24z" />
        <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a11.9 11.9 0 0 0 0 10.8l4-3.1z" />
        <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
      </svg>
      Continue with Google
    </button>
  );

  return (
    <div className="grain min-h-screen bg-ink text-paper font-body" data-testid="account-page">
      <header className="sticky top-0 z-40 bg-ink/85 backdrop-blur-xl border-b border-edge">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="account-logo-link" className="flex items-center gap-3">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-display font-semibold tracking-tight text-paper">My Account</span>
              <span className="block font-mono text-[10px] tracking-[0.35em] text-mist mt-1">PRO PRINT SOLUTION LLP</span>
            </span>
          </Link>
          <Link to="/" data-testid="account-home-link" className="inline-flex items-center gap-2 bg-paper text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-cyanbrand transition-colors">
            Home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {user ? (
          <>
            <div className="flex items-center gap-4 border border-edge bg-surface rounded-sm p-6" data-testid="account-profile-card">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-edge" />
              ) : (
                <span className="w-12 h-12 rounded-full bg-cyanbrand/10 border border-cyanbrand/40 grid place-items-center text-cyanbrand">
                  <User size={20} />
                </span>
              )}
              <div className="grow">
                <p className="font-display font-semibold text-lg text-paper" data-testid="account-user-name">{user.name || user.email}</p>
                <p className="font-mono text-[11px] text-mist mt-1" data-testid="account-user-email">{user.email}</p>
              </div>
              <button
                onClick={logout}
                data-testid="account-logout-button"
                className="inline-flex items-center gap-2 border border-edge text-mist hover:text-mag hover:border-mag font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm transition-colors"
              >
                <LogOut size={13} /> Sign Out
              </button>
            </div>

            <h2 className="mt-12 font-display font-semibold tracking-tight text-2xl sm:text-3xl text-paper flex items-center gap-3">
              <MessageSquare size={22} className="text-cyanbrand" /> My Enquiries
            </h2>

            {enquiries.length === 0 ? (
              <div className="mt-6 border border-dashed border-edge rounded-sm py-16 text-center" data-testid="account-enquiries-empty">
                <MessageSquare size={26} className="mx-auto text-mist/40" />
                <p className="mt-4 font-mono text-xs tracking-[0.2em] text-mist">NO ENQUIRIES YET</p>
                <p className="mt-2 text-sm text-mist/60">Enquiries you send with this email will show up here with their status.</p>
                <Link to="/" className="inline-block mt-6 bg-cyanbrand text-ink font-bold text-sm px-6 py-3 rounded-sm hover:bg-paper transition-colors">
                  Send your first enquiry
                </Link>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {enquiries.map((en, i) => (
                  <article key={en.id} data-testid={`account-enquiry-${i}`} className="border border-edge bg-surface rounded-sm p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyanbrand">{en.service}</span>
                      <p className="font-mono text-[10px] text-mist/60 mt-2">
                        {new Date(en.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                    <p className="sm:col-span-6 text-sm text-paper/80 leading-relaxed">{en.message || "—"}</p>
                    <span className={`sm:col-span-2 sm:justify-self-end font-mono text-xs ${STATUS_COLORS[en.status] || "text-mist"}`} data-testid={`account-enquiry-status-${i}`}>
                      {STATUSES[en.status] || en.status}
                    </span>
                  </article>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="max-w-md mx-auto border border-edge bg-surface rounded-sm p-8" data-testid="account-auth-card">
            <div className="flex items-center gap-3 mb-8">
              <LogoMark className="w-9 h-9" />
              <div>
                <p className="font-display font-semibold text-paper">Customer Login</p>
                <p className="font-mono text-[10px] tracking-[0.25em] text-mist">TRACK YOUR PRINT JOBS</p>
              </div>
            </div>

            {googleBtn}

            <div className="flex items-center gap-3 my-6">
              <span className="h-px grow bg-edge" />
              <span className="font-mono text-[10px] tracking-[0.2em] text-mist">OR EMAIL</span>
              <span className="h-px grow bg-edge" />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-6">
              <button
                type="button"
                data-testid="account-tab-signin"
                onClick={() => setTab("signin")}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] py-2.5 rounded-sm border transition-colors ${tab === "signin" ? "bg-cyanbrand text-ink border-cyanbrand font-semibold" : "border-edge text-mist hover:text-paper"}`}
              >
                Sign In
              </button>
              <button
                type="button"
                data-testid="account-tab-register"
                onClick={() => setTab("register")}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] py-2.5 rounded-sm border transition-colors ${tab === "register" ? "bg-cyanbrand text-ink border-cyanbrand font-semibold" : "border-edge text-mist hover:text-paper"}`}
              >
                Sign Up
              </button>
            </div>

            {tab === "signin" ? (
              <form onSubmit={login} data-testid="account-login-form">
                <label htmlFor="acc-email" className={labelCls}>Email</label>
                <input id="acc-email" required type="email" data-testid="account-email-input" className={`${inputCls} mb-4`} value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} placeholder="you@gmail.com" />
                <label htmlFor="acc-password" className={labelCls}>Password</label>
                <input id="acc-password" required type="password" data-testid="account-password-input" className={`${inputCls} mb-6`} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••" />
                <button type="submit" data-testid="account-login-button" className="w-full bg-cyanbrand text-ink font-bold text-sm py-3.5 rounded-sm hover:bg-paper transition-colors">
                  Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={register} data-testid="account-register-form">
                <label htmlFor="reg-name" className={labelCls}>Full Name</label>
                <input id="reg-name" required data-testid="account-name-input" className={`${inputCls} mb-4`} value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Your name" />
                <label htmlFor="reg-email" className={labelCls}>Email</label>
                <input id="reg-email" required type="email" data-testid="account-register-email-input" className={`${inputCls} mb-4`} value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="you@gmail.com" />
                <label htmlFor="reg-password" className={labelCls}>Password</label>
                <input id="reg-password" required type="password" minLength={6} data-testid="account-register-password-input" className={`${inputCls} mb-6`} value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="Min 6 characters" />
                <button type="submit" data-testid="account-register-button" className="w-full bg-cyanbrand text-ink font-bold text-sm py-3.5 rounded-sm hover:bg-paper transition-colors">
                  Create Account
                </button>
              </form>
            )}

            <p className="mt-6 font-mono text-[10px] text-mist/60 leading-relaxed text-center">
              Enquiries sent with your email appear here with live status.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
